import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
/** components **/
import { SurveySimpleLayout } from 'components/organisms';
/** api **/
import { db } from 'api/firebase';
/** styles **/
import s from '../styles/pages/survey-form.module.scss';

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_3505_16369)">
      <path d="M13.2218 2.77751C13.8575 2.14216 14.8879 2.14215 15.5236 2.77751C16.1594 3.41329 16.1594 4.44446 15.5236 5.08025L7.3937 13.2092C7.39015 13.2128 7.38752 13.2173 7.38393 13.2209C7.02811 13.5767 6.54892 13.7331 6.08413 13.6906C5.9596 13.6793 5.8367 13.6523 5.71694 13.6125C5.65753 13.5926 5.59939 13.569 5.54214 13.5422C5.37561 13.4642 5.21876 13.3584 5.0812 13.2209C5.07783 13.2175 5.07476 13.2135 5.07143 13.2101L0.476706 8.61638C-0.158815 7.98057 -0.158989 6.94934 0.476706 6.31364C1.1124 5.67797 2.14364 5.67813 2.77944 6.31364L6.23159 9.76677L13.2218 2.77751Z" fill="white"/>
    </g>
    <defs>
      <clipPath id="clip0_3505_16369">
        <rect width="16" height="16" rx="2" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

export const SurveyForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data from previous pages
  const selectedFloorPlan = location.state?.selectedFloorPlan || '';
  const mansion01Plan = location.state?.mansion01Plan || null;
  const mansion02Plan = location.state?.mansion02Plan || null;
  
  // Form state
  const [q1Answer, setQ1Answer] = useState<string>('');
  const [q2Answer, setQ2Answer] = useState<string>('');
  const [q3Answer, setQ3Answer] = useState<string>('');
  const [q4Answer, setQ4Answer] = useState<string>('');
  const [q4DropdownOpen, setQ4DropdownOpen] = useState<boolean>(false);
  const [q5Answer, setQ5Answer] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  
  // Check if all questions are answered
  const isFormComplete = q1Answer && q2Answer && q3Answer && q4Answer !== '' && q5Answer.length > 0;
  
  const handleQ5Toggle = (pet: string) => {
    if (pet === '飼っていない') {
      // 「飼っていない」を選択した場合、他のペットの選択を解除
      setQ5Answer(prev => 
        prev.includes(pet) 
          ? [] 
          : [pet]
      );
    } else {
      // 他のペットを選択した場合
      setQ5Answer(prev => {
        // 「飼っていない」が選択されている場合は解除
        const filtered = prev.filter(p => p !== '飼っていない');
        
        if (filtered.includes(pet)) {
          // 既に選択されている場合は解除
          return filtered.filter(p => p !== pet);
        } else {
          // 新たに選択
          return [...filtered, pet];
        }
      });
    }
  };
  
  const handleSubmit = async () => {
    if (!isFormComplete || isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Prepare survey data
      const surveyData = {
        timestamp: new Date(),
        floorPlan: selectedFloorPlan,
        mansion01Plan: mansion01Plan,
        mansion02Plan: mansion02Plan,
        q1_supporter: q1Answer,
        q2_gender: q2Answer,
        q3_age: parseInt(q3Answer),
        q4_livingArrangement: q4Answer,
        q5_pets: q5Answer.join('、'), // 配列を「、」で結合した文字列に変換
      };
      
      // Save to Firebase Firestore
      await addDoc(collection(db, 'survey-responses'), surveyData);
      
      // Navigate to thanks page based on Q1 answer
      if (q1Answer === 'はい') {
        navigate('/survey/thanks-member');
      } else {
        navigate('/survey/thanks');
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('アンケートの送信に失敗しました。もう一度お試しください。');
      setIsSubmitting(false);
    }
  };

  return (
    <SurveySimpleLayout>
      <div className={s.surveyFormWrapper}>
        {/* ステップインジケーター */}
        <section className={s.stepIndicator}>
          <div className={s.stepWrapper}>
            <div className={`${s.step} ${s.completed}`}>
              <span>01</span>
            </div>
            <div className={`${s.step} ${s.completed}`}>
              <span>02</span>
            </div>
            <div className={`${s.step} ${s.active}`}>
              <span>03</span>
            </div>
          </div>
        </section>

        <h1 className={s.pageTitle}>アンケート</h1>
        
        {/* Q1 */}
        <section className={s.questionSection}>
          <div className={s.questionContent}>
            <h2 className={s.questionNumber}>Q1.</h2>
            <p className={s.questionText}>
              「P-man」サポーターに<br />
              すでに登録されていますか？
            </p>
            <div className={s.optionButtons}>
              <button
                className={`${s.optionButton} ${q1Answer === 'いいえ' ? s.selected : ''}`}
                onClick={() => setQ1Answer('いいえ')}
              >
                いいえ
              </button>
              <button
                className={`${s.optionButton} ${q1Answer === 'はい' ? s.selected : ''}`}
                onClick={() => setQ1Answer('はい')}
              >
                はい
              </button>
            </div>
          </div>
        </section>
        
        {/* Q2 */}
        <section className={s.questionSection}>
          <div className={s.questionContent}>
            <h2 className={s.questionNumber}>Q2.</h2>
            <p className={s.questionText}>性別を教えてください</p>
            <div className={s.optionButtons}>
              <button
                className={`${s.optionButton} ${q2Answer === '男性' ? s.selected : ''}`}
                onClick={() => setQ2Answer('男性')}
              >
                男性
              </button>
              <button
                className={`${s.optionButton} ${q2Answer === '女性' ? s.selected : ''}`}
                onClick={() => setQ2Answer('女性')}
              >
                女性
              </button>
            </div>
          </div>
        </section>
        
        {/* Q3 */}
        <section className={s.questionSection}>
          <div className={s.questionContent}>
            <h2 className={s.questionNumber}>Q3.</h2>
            <p className={s.questionText}>年齢をおしえてください</p>
            <div className={s.numberInput}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={q3Answer}
                onChange={(e) => {
                  // 数値のみを許可（整数のみ）
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  // 3桁まで制限
                  if (value.length <= 3) {
                    setQ3Answer(value);
                  }
                }}
                onKeyDown={(e) => {
                  // 小数点、e、+、-を入力できないようにする
                  if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="00"
                className={q3Answer ? s.inputed : ''}
              />
              <span className={s.unit}>歳</span>
            </div>
          </div>
        </section>
        
        {/* Q4 */}
        <section className={s.questionSection}>
          <div className={s.questionContent}>
            <h2 className={s.questionNumber}>Q4.</h2>
            <p className={s.questionText}>
              今の同居形態を<br />
              教えてください
            </p>
            <div className={s.customSelectWrapper}>
              <button
                className={`${s.customSelectButton} ${q4DropdownOpen ? s.active : ''} ${q4Answer ? s.selected : ''}`}
                onClick={() => setQ4DropdownOpen(!q4DropdownOpen)}
                type="button"
              >
                <span className={q4Answer ? s.selectedText : s.placeholder}>
                  {q4Answer || '選んでください'}
                </span>
                <span className={s.dropdownIcon}>▼</span>
              </button>
              {q4DropdownOpen && (
                <div className={s.customDropdown}>
                  <button
                    className={s.dropdownOption}
                    onClick={() => {
                      setQ4Answer('1人暮らし');
                      setQ4DropdownOpen(false);
                    }}
                    type="button"
                  >
                    1人暮らし
                  </button>
                  <button
                    className={s.dropdownOption}
                    onClick={() => {
                      setQ4Answer('2人暮らし（夫婦・カップル）');
                      setQ4DropdownOpen(false);
                    }}
                    type="button"
                  >
                    2人暮らし（夫婦・カップル）
                  </button>
                  <button
                    className={s.dropdownOption}
                    onClick={() => {
                      setQ4Answer('2人暮らし（親子供・他）');
                      setQ4DropdownOpen(false);
                    }}
                    type="button"
                  >
                    2人暮らし（親子供・他）
                  </button>
                  <button
                    className={s.dropdownOption}
                    onClick={() => {
                      setQ4Answer('3人以上');
                      setQ4DropdownOpen(false);
                    }}
                    type="button"
                  >
                    3人以上
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
        
        {/* Q5 */}
        <section className={s.questionSection}>
          <div className={s.questionContent}>
            <h2 className={s.questionNumber}>Q5.</h2>
            <p className={s.questionText}>
              現在飼っているペットは<br />
              何ですか？
            </p>
            <div className={s.multiOptionButtons}>
              {['小型犬', '中型犬', '大型犬', '猫', 'その他', '飼っていない'].map((pet) => {
                const isDisabled = 
                  (pet === '飼っていない' && q5Answer.some(p => p !== '飼っていない')) ||
                  (pet !== '飼っていない' && q5Answer.includes('飼っていない'));
                
                return (
                  <button
                    key={pet}
                    className={`${s.multiOptionButton} ${q5Answer.includes(pet) ? s.selected : ''} ${isDisabled ? s.disabled : ''}`}
                    onClick={() => !isDisabled && handleQ5Toggle(pet)}
                    disabled={isDisabled}
                  >
                    {q5Answer.includes(pet) && <CheckIcon />}
                    {pet}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
        
        {/* Navigation */}
        <div className={s.navigationSection}>
          <div className={s.navigationButtons}>
          <Link to="/survey/rent02" className={s.backButton} state={{ selectedFloorPlan, mansion01Plan }}>
            戻る
          </Link>
          <button 
            className={s.proceedButton} 
            onClick={handleSubmit}
            disabled={!isFormComplete || isSubmitting}
          >
            <span className={s.proceedButtonText}>
              {isSubmitting ? '送信中...' : '送信'}
            </span>
            {!isSubmitting && (
              <svg className={s.proceedButtonIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="20" height="20" rx="10" fill="#00B66A"/>
                <path d="M8 6L12 10L8 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
          </div>
          {!isFormComplete && (
            <p className={s.formHint}>全ての項目に入力すると進めます</p>
          )}
        </div>
      </div>
    </SurveySimpleLayout>
  );
};